import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, PlayCircle, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { logDataProcessing } from '@/lib/compliance-logger';

interface QualityCheck {
  check_name: string;
  severity: 'error' | 'warning' | 'info';
  affected_count: number;
  table_name: string;
  description: string;
}

const SEVERITY_CONFIG = {
  error: { icon: AlertCircle, color: 'destructive' as const, label: 'Error' },
  warning: { icon: AlertTriangle, color: 'secondary' as const, label: 'Warning' },
  info: { icon: Info, color: 'outline' as const, label: 'Info' },
};

const AdminDataQualityTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [results, setResults] = useState<QualityCheck[] | null>(null);
  const [loading, setLoading] = useState(false);

  const runChecks = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('run_data_quality_checks');
      if (error) throw error;

      const checks = data as unknown as QualityCheck[];
      setResults(checks);

      // Log to data_processing_log
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const errorCount = checks.filter(c => c.severity === 'error').length;
        const warningCount = checks.filter(c => c.severity === 'warning').length;
        logDataProcessing({
          actorId: user.id,
          actorType: 'admin',
          action: 'access',
          resourceType: 'data_quality_report',
          details: `Data quality checks: ${errorCount} errors, ${warningCount} warnings, ${checks.filter(c => c.severity === 'info').length} info`,
        });
      }

      toast({
        title: t('dataManagement.dataQuality.checksComplete', 'Checks Complete'),
        description: t('dataManagement.dataQuality.checksCompleteDesc', 'Data quality checks have been run successfully.'),
      });
    } catch (err: any) {
      toast({
        title: t('dataManagement.dataQuality.checksFailed', 'Checks Failed'),
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const errorCount = results?.filter(c => c.severity === 'error').reduce((s, c) => s + c.affected_count, 0) ?? 0;
  const warningCount = results?.filter(c => c.severity === 'warning').reduce((s, c) => s + c.affected_count, 0) ?? 0;
  const infoCount = results?.filter(c => c.severity === 'info').reduce((s, c) => s + c.affected_count, 0) ?? 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{t('dataManagement.dataQuality.title', 'Data Quality')}</CardTitle>
          <CardDescription>
            {t('dataManagement.dataQuality.description', 'Run integrity checks to detect orphaned records, incomplete profiles, and data anomalies.')}
          </CardDescription>
        </div>
        <Button onClick={runChecks} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <PlayCircle className="h-4 w-4 mr-2" />
          )}
          {t('dataManagement.dataQuality.runChecks', 'Run Quality Checks')}
        </Button>
      </CardHeader>
      <CardContent>
        {results === null ? (
          <p className="text-muted-foreground text-center py-8">
            {t('dataManagement.dataQuality.noResults', 'Click "Run Quality Checks" to analyze your data.')}
          </p>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 border rounded-lg">
                <div className={`text-2xl font-bold ${errorCount > 0 ? 'text-destructive' : ''}`}>{errorCount}</div>
                <div className="text-xs text-muted-foreground">Errors</div>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <div className={`text-2xl font-bold ${warningCount > 0 ? 'text-yellow-600' : ''}`}>{warningCount}</div>
                <div className="text-xs text-muted-foreground">Warnings</div>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <div className="text-2xl font-bold">{infoCount}</div>
                <div className="text-xs text-muted-foreground">Info</div>
              </div>
            </div>

            {/* Results Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Check</TableHead>
                  <TableHead>Table</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead className="text-right">Affected</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((check, i) => {
                  const config = SEVERITY_CONFIG[check.severity];
                  const Icon = config.icon;
                  return (
                    <TableRow key={i}>
                      <TableCell className="font-medium text-sm">{check.check_name}</TableCell>
                      <TableCell className="font-mono text-xs">{check.table_name}</TableCell>
                      <TableCell>
                        <Badge variant={config.color} className="gap-1">
                          <Icon className="h-3 w-3" />
                          {config.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={check.affected_count > 0 ? 'font-bold' : 'text-muted-foreground'}>
                          {check.affected_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{check.description}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminDataQualityTab;
